import React, { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Checkbox } from '../ui/Checkbox'
import { useFoldersData } from '@/hooks/UseFoldersData.ts'
import type { ContentType } from '@/db/Types.ts'
import './add-to-folder-dialog.css'

interface AddToFolderDialogProps {
	isOpen: boolean
	onClose: () => void
	selectedItems: Array<{ type: ContentType; id: string }>
	onAddToFolders: (folderIds: string[], items: Array<{ type: ContentType; id: string }>) => Promise<void>
}

export function AddToFolderDialog({
									  isOpen,
									  onClose,
									  selectedItems,
									  onAddToFolders,
								  }: AddToFolderDialogProps) {
	const { folders, createFolder } = useFoldersData()
	const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set())
	const [newFolderName, setNewFolderName] = useState('')
	const [isCreatingFolder, setIsCreatingFolder] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	// Reset state when dialog opens/closes
	useEffect(() => {
		if (!isOpen) {
			setSelectedFolders(new Set())
			setNewFolderName('')
			setIsCreatingFolder(false)
		}
	}, [isOpen])

	const toggleFolder = (folderId: string) => {
		setSelectedFolders((prev) => {
			const next = new Set(prev)
			next.has(folderId) ? next.delete(folderId) : next.add(folderId)
			return next
		})
	}

	const handleCreateFolder = async () => {
		if (!newFolderName.trim()) return

		setIsCreatingFolder(true)
		try {
			const newFolder = await createFolder(newFolderName.trim())
			setSelectedFolders((prev) => new Set(prev).add(newFolder.id))
			setNewFolderName('')
		} finally {
			setIsCreatingFolder(false)
		}
	}

	const handleSubmit = async () => {
		if (selectedFolders.size === 0) return

		setIsSubmitting(true)
		try {
			await onAddToFolders(Array.from(selectedFolders), selectedItems)
			onClose()
		} finally {
			setIsSubmitting(false)
		}
	}

	const hasChanges = selectedFolders.size > 0

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="add-to-folder-dialog">
				<DialogHeader>
					<DialogTitle>Add to Folder</DialogTitle>
					<DialogDescription>
						Select folders to add {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} to
					</DialogDescription>
				</DialogHeader>

				<div className="add-to-folder-dialog__content">
					{/* Folder list */}
					{folders.length > 0 ? (
						<div className="add-to-folder-dialog__list">
							{folders.map((folder, index) => (
								<label
									key={folder.id}
									className="add-to-folder-dialog__item"
									style={{ '--item-index': index } as React.CSSProperties}
								>
									<Checkbox
										checked={selectedFolders.has(folder.id)}
										onCheckedChange={() => toggleFolder(folder.id)}
									/>
									<span className="add-to-folder-dialog__folder-name">
										{folder.name}
									</span>
								</label>
							))}
						</div>
					) : (
						<p className="add-to-folder-dialog__empty">
							No folders yet. Create one below to get started.
						</p>
					)}

					{/* Create new folder */}
					<div className="add-to-folder-dialog__create">
						<Input
							type="text"
							value={newFolderName}
							onChange={(e) => setNewFolderName(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									e.preventDefault()
									handleCreateFolder()
								}
							}}
							placeholder="Create new folder..."
							disabled={isCreatingFolder}
						/>
						<Button
							variant="outline"
							size="sm"
							onClick={handleCreateFolder}
							disabled={!newFolderName.trim() || isCreatingFolder}
						>
							<Plus size={16} />
						</Button>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={isSubmitting}>
						Cancel
					</Button>
					<Button
						variant="action"
						onClick={handleSubmit}
						disabled={!hasChanges || isSubmitting}
					>
						{isSubmitting ? 'Adding...' : 'Add to Folders'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
